/**
 * Frontend mirror of backend project-modules tab bindings.
 *
 * Source of truth: backend/src/project-modules/project-modules.registry.ts
 * Keep module codes and tab keys in sync when editing either side.
 *
 * Used by capabilities merge (superAdminTabs) once project.features is wired.
 *
 * Upgrade / докупки model:
 *   - Module add-ons: catalog.project.features.modules['<code>'].enabled = true
 *   - Quota top-ups: catalog.project.quotas['<code>'] = <number|null>
 *   See backend types for full description.
 */

/** @typedef {'core.live_data'|'core.alarms_view'|'core.users_basic'|'core.layout_basic'|'core.cards'|'core.code_groups'|'core.uid_management'|'commands_basic'|'trends'|'telegram'|'telegram_advanced'|'svg_hmi'|'project_tags'|'project_alarm_rules'|'commands_advanced'|'maps'|'debug_tools'} ProjectModuleCode */

/**
 * Maps product module → admin shell tabs (superAdminTabs TAB_PERMISSION_KEYS).
 * `subs` lists local/layout sub-tab ids when the whole parent tab stays visible.
 *
 * Multiple modules may bind to the same (tab, sub) pair (e.g. core.uid_management
 * and commands_advanced both touch device.uid). A tab/sub is hidden only when
 * NO enabled module exposes content there — see `getDisabledTabHintsFromModules`.
 */
export const MODULE_TAB_BINDINGS = Object.freeze({
  'core.live_data': [{ key: 'project.overview' }],
  'core.users_basic': [{ key: 'users' }],
  'core.layout_basic': [{ key: 'layout', subs: ['sets', 'groups'] }],
  'core.cards': [{ key: 'layout', subs: ['cards'] }],
  'core.code_groups': [{ key: 'layout', subs: ['lists'] }],
  'core.uid_management': [{ key: 'device.uid' }],
  trends: [{ key: 'local', subs: ['trends'] }],
  telegram: [{ key: 'telegram' }],
  telegram_advanced: [{ key: 'device.uid' }],
  svg_hmi: [],
  project_tags: [{ key: 'local', subs: ['tags'] }],
  project_alarm_rules: [{ key: 'local', subs: ['alarms'] }],
  commands_advanced: [{ key: 'device.uid' }],
});

export const PROJECT_PLAN_CODES = Object.freeze([
  'legacy_full',
  'starter',
  'pro',
  'enterprise',
]);

/** Default until catalog.project.plan_code column exists */
export const DEFAULT_NEW_PROJECT_PLAN = 'legacy_full';

/**
 * Given enabled module map, return tab keys/subs that should be treated as
 * unavailable. A tab/sub is hidden only when NO enabled module covers it.
 *
 * @param {Record<string, boolean>|null|undefined} enabledModules
 * @returns {Array<{ key: string, subs?: string[] }>}
 */
export function getDisabledTabHintsFromModules(enabledModules) {
  if (!enabledModules || typeof enabledModules !== 'object') return [];

  const enabledFullTabs = new Set();
  const enabledTabSubs = new Map();

  for (const [moduleCode, bindings] of Object.entries(MODULE_TAB_BINDINGS)) {
    if (!enabledModules[moduleCode]) continue;
    for (const b of bindings || []) {
      if (!b.subs || !b.subs.length) {
        enabledFullTabs.add(b.key);
        continue;
      }
      const set = enabledTabSubs.get(b.key) || new Set();
      for (const sub of b.subs) set.add(sub);
      enabledTabSubs.set(b.key, set);
    }
  }

  const out = [];

  for (const [moduleCode, bindings] of Object.entries(MODULE_TAB_BINDINGS)) {
    if (enabledModules[moduleCode]) continue;
    for (const b of bindings || []) {
      if (!b.subs || !b.subs.length) {
        if (enabledFullTabs.has(b.key)) continue;
        if (enabledTabSubs.has(b.key)) continue;
        out.push({ key: b.key });
        continue;
      }
      if (enabledFullTabs.has(b.key)) continue;
      const enabledSubs = enabledTabSubs.get(b.key) || new Set();
      const stillDisabled = b.subs.filter((sub) => !enabledSubs.has(sub));
      if (!stillDisabled.length) continue;
      out.push({ key: b.key, subs: stillDisabled });
    }
  }

  return out;
}

/**
 * Apply product module gates on top of user capabilities tabs object.
 * Does not mutate input.
 *
 * @param {object|null|undefined} capabilities — effective user ACL
 * @param {Record<string, boolean>|null|undefined} enabledModules — from project.features
 */
export function applyModuleGatesToCapabilities(capabilities, enabledModules) {
  if (!capabilities || capabilities.tabs === '*') {
    return capabilities;
  }
  const hints = getDisabledTabHintsFromModules(enabledModules);
  if (!hints.length) return capabilities;

  const tabs = { ...(capabilities.tabs || {}) };
  for (const hint of hints) {
    const prev = tabs[hint.key];
    if (!prev || typeof prev !== 'object') {
      tabs[hint.key] = { read: false, write: false, delete: false };
      continue;
    }
    if (hint.subs?.length && prev.subs && typeof prev.subs === 'object') {
      const subs = { ...prev.subs };
      for (const subId of hint.subs) {
        subs[subId] = { read: false, write: false, delete: false };
      }
      tabs[hint.key] = { ...prev, subs };
    } else if (!hint.subs?.length) {
      tabs[hint.key] = { read: false, write: false, delete: false };
    }
  }
  return { ...capabilities, tabs };
}

/**
 * Whether superadmin may grant ACL on a top-level tab in PermissionsMatrix.
 * Tabs with subs are grantable when at least one sub-tab module is enabled.
 */
export function isTopTabPermissionGrantable(enabledModules, tabKey, subKeys = []) {
  if (!enabledModules || typeof enabledModules !== 'object') return true;
  if (!Array.isArray(subKeys) || subKeys.length === 0) {
    const hints = getDisabledTabHintsFromModules(enabledModules);
    return !hints.some((h) => h.key === tabKey && !h.subs?.length);
  }
  return subKeys.some((subKey) =>
    isSubPermissionGrantable(enabledModules, tabKey, subKey),
  );
}

/** Whether superadmin may grant ACL on a sub-tab row (Local / Layout). */
export function isSubPermissionGrantable(enabledModules, tabKey, subKey) {
  if (!enabledModules || typeof enabledModules !== 'object') return true;
  const hints = getDisabledTabHintsFromModules(enabledModules);
  for (const h of hints) {
    if (h.key !== tabKey) continue;
    if (!h.subs?.length) return false;
    if (h.subs.includes(subKey)) return false;
  }
  return true;
}

/**
 * Strip ACL bits for tabs/subs whose product modules are disabled.
 * Used when saving admin policy and when applying presets.
 */
export function sanitizePermissionsForModules(permissions, enabledModules) {
  if (!permissions || typeof permissions !== 'object') return permissions;
  if (!enabledModules || typeof enabledModules !== 'object') return permissions;
  return applyModuleGatesToCapabilities(permissions, enabledModules);
}

/** Явная ACL policy: хотя бы один tab/sub с read=true. null/undefined → false. */
export function permissionsHasAnyReadAccess(permissions) {
  if (!permissions || typeof permissions !== 'object') return false;
  if (permissions.tabs === '*') return true;
  const tabs = permissions.tabs;
  if (!tabs || typeof tabs !== 'object') return false;
  for (const tab of Object.values(tabs)) {
    if (!tab || typeof tab !== 'object') continue;
    if (tab.read === true) return true;
    if (tab.subs && typeof tab.subs === 'object') {
      for (const sub of Object.values(tab.subs)) {
        if (sub?.read === true) return true;
      }
    }
  }
  return false;
}

/** Для валидации формы: null = дефолт по роли (OK), объект без read — ошибка. */
export function validateExplicitAdminPermissions(permissions) {
  if (permissions === null || permissions === undefined) return null;
  if (!permissionsHasAnyReadAccess(permissions)) {
    return 'Admin must have read access to at least one panel tab';
  }
  return null;
}
